import {
  AccountingReportType,
  ClientType,
  ENDPOINTS,
  Status,
  WebTable
} from '@constants';
import { Prototype } from '@core';
import {
  useExportOperationReportData,
  useMutateFetchOperationReportData
} from '@hooks/report/operation';
import { IReportAccountingFilter } from '@pages/ReportV2/helpers';
import { AgUtils } from '@utils';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { map } from 'lodash';
import { memo, useCallback, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import trans from 'translation';
import { KChip, KContainer, KForm, KGrid, KInput, KLabel } from 'uikit';

import Item from '../../_components/Item';

interface IFormData extends Pick<IReportAccountingFilter, 'ars' | 'isExport'> {}

const ReportStatement = () => {
  const colDefs = useMemo<ColDef<any>[]>(
    () => [
      {
        headerName: trans('code'),
        field: 'code',
        pinned: true,
        minWidth: 200
      },
      {
        headerName: trans('name'),
        field: 'name',
        pinned: true,
        cellClass: 'ag-cell-full-width',
        cellRenderer: ({ value = null, node }: ICellRendererParams) => {
          return (
            <KContainer.View
              dp="flex"
              row
              justifyContent={node.rowPinned ? 'flex-end' : 'flex-start'}
            >
              <KLabel.Text
                typo={node.rowPinned ? 'TextNmBold' : 'TextNmNormal'}
              >
                {value}
              </KLabel.Text>
            </KContainer.View>
          );
        }
      },
      { headerName: trans('email'), field: 'email' },
      { headerName: trans('address'), field: 'address' },
      {
        headerName: trans('outstanding_balance'),
        field: 'outstandingBalance',
        ...AgUtils.options.currency()
      },
      {
        headerName: trans('overdue'),
        field: 'overdue',
        ...AgUtils.options.currency()
      },
      {
        headerName: trans('total_invoices'),
        field: 'totalInvoices',
        ...AgUtils.options.currency()
      },
      {
        headerName: trans('total_payments'),
        field: 'totalPayments',
        ...AgUtils.options.currency()
      },
      {
        headerName: trans('total_jobs'),
        field: 'totalJobs'
      },
      {
        headerName: trans('total_containers'),
        field: 'totalContainers'
      }
    ],
    []
  );

  const {
    data,
    mutate: mutateGenerate,
    isLoading
  } = useMutateFetchOperationReportData();

  const { mutate: mutateExport, isLoading: isExporting } =
    useExportOperationReportData();

  const methods = useForm<IFormData>({
    defaultValues: {
      isExport: false,
      ars: []
    }
  });

  const onFormValid = useCallback(
    (_data: IFormData) => {
      const { isExport, ars, ...rest } = _data;

      const arIds = map(ars, 'id');

      const _params = {
        reportExcelType: AccountingReportType.STATEMENT.split('_')[0],
        arIds,
        ...rest
      };

      const mutate = isExport ? mutateExport : mutateGenerate;

      mutate(_params);
    },
    [mutateExport, mutateGenerate]
  );

  const renderPinnedBottomRowDataFunc = useCallback((_data: any) => {
    return [
      {
        code: trans('total_outstanding_balance'),
        name: Prototype.number.formatCurrency(
          _data?.totalOutstandingBalance ?? 0
        )
      },
      {
        code: trans('total_overdue'),
        name: Prototype.number.formatCurrency(_data?.totalOverdue ?? 0)
      },
      {
        code: trans('total_invoices'),
        name: _data?.totalInvoices ?? 0
      },
      {
        code: trans('total_payments'),
        name: _data?.totalPayments ?? 0
      },
      {
        code: trans('total_jobs'),
        name: _data?.totalJobs ?? 0
      },
      {
        code: trans('total_containers'),
        name: _data?.totalContainers ?? 0
      },
      {
        code: trans('average_outstanding_balance_per_client'),
        name: Prototype.number.formatCurrency(
          _data?.averageOutstandingBalancePerClient ?? 0
        )
      },
      {
        code: trans('average_overdue_per_client'),
        name: Prototype.number.formatCurrency(
          _data?.averageOverduePerClient ?? 0
        )
      },
      {
        code: trans('average_invoices_per_client'),
        name: _data?.averageInvoicesPerClient ?? 0
      },
      {
        code: trans('average_payments_per_client'),
        name: _data?.averagePaymentsPerClient ?? 0
      },
      {
        code: trans('average_jobs_per_client'),
        name: _data?.averageJobsPerClient ?? 0
      },
      {
        code: trans('average_containers_per_client'),
        name: _data?.averageContainersPerClient ?? 0
      }
    ];
  }, []);

  return (
    <FormProvider {...methods}>
      <KForm onSubmit={methods.handleSubmit(onFormValid)}>
        <KGrid.Container>
          <KGrid.Item xs={12}>
            <Controller
              name="ars"
              control={methods.control}
              render={({ field }) => {
                return (
                  <KInput.Autocomplete
                    {...field}
                    label={trans('ar_client')}
                    multiple
                    renderTags={(tagValue, getTagProps) => {
                      return tagValue?.map((i: any, index) => {
                        const isActive = i?.status === Status.Active;
                        return (
                          <KChip
                            key={i.id}
                            {...getTagProps({ index })}
                            label={i.name}
                            isLink
                            onPress={() => {
                              window.open(`/admin/clients/${i.id}`);
                            }}
                            color={isActive ? undefined : 'secondary'}
                          />
                        );
                      });
                    }}
                    apiURL={ENDPOINTS.client()}
                    apiParams={{
                      typeIn: [ClientType.AccountReceivable],
                      status: Status.Active
                    }}
                  />
                );
              }}
            />
          </KGrid.Item>
          <KGrid.Item xs={12}>
            <Item
              name={WebTable.ACCOUNTING_REPORT_STATEMENT}
              colDefs={colDefs}
              data={data}
              onSubmit={onFormValid}
              isGenerating={isLoading}
              isExporting={isExporting}
              renderPinnedBottomRowDataFunc={renderPinnedBottomRowDataFunc}
              height={1000}
            />
          </KGrid.Item>
        </KGrid.Container>
      </KForm>
    </FormProvider>
  );
};

export default memo(ReportStatement);
